import {
  VideoTrack, isTrackReference, useIsSpeaking, useLocalParticipant, useTracks,
  type TrackReferenceOrPlaceholder,
} from '@livekit/components-react';
import { Track, type Participant } from 'livekit-client';
import { MicOff } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import {
  Box, Center, HStack, Icon, Text,
} from '@gluestack-ui/themed';

// Absolute fill keeps the <video> element sized to its container instead of its
// intrinsic resolution, so it can never overflow or push sibling layout.
const FILL_VIDEO_STYLE = {
  position: 'absolute' as const,
  top: 0,
  left: 0,
  width: '100%' as const,
  height: '100%' as const,
  objectFit: 'cover' as const,
};

function participantInitials(participant: Participant): string {
  const name = participant.name || participant.identity || '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
  }
  return (name[0] ?? '?').toUpperCase();
}

type ParticipantTileProps = {
  trackRef: TrackReferenceOrPlaceholder;
  rounded?: 'md' | 'lg';
  initialsSize?: '2xl' | '4xl';
  /** When false, omit the bottom name/mute badge (caller renders a caption elsewhere). */
  showNameBadge?: boolean;
};

/**
 * A single participant surface that always fills its parent container. The
 * parent decides the size (speaker stage, pip overlay, or thumbnail); the tile
 * only renders the video (or an initials placeholder) plus a name/mute badge.
 */
export function ParticipantTile({
  trackRef, rounded = 'md', initialsSize = '2xl', showNameBadge = true,
}: ParticipantTileProps) {
  const { t } = useTranslation();
  const { localParticipant } = useLocalParticipant();
  const isSpeaking = useIsSpeaking(trackRef.participant);
  const { isLocal } = trackRef.participant;
  const isMuted = isLocal
    ? !localParticipant.isMicrophoneEnabled
    : !trackRef.participant.isMicrophoneEnabled;
  const label = isLocal
    ? t('meeting.you')
    : (trackRef.participant.name || trackRef.participant.identity);
  const hasVideo = isTrackReference(trackRef)
    && Boolean(trackRef.publication.track)
    && !trackRef.publication.isMuted;

  return (
    <Box
      width="100%"
      height="100%"
      position="relative"
      overflow="hidden"
      borderRadius={rounded === 'lg' ? '$lg' : '$md'}
      bg="$bgDark"
      borderWidth={isSpeaking ? 2 : 1}
      borderColor={isSpeaking ? '$primary500' : '$borderLight'}
      testID={isLocal ? 'meeting-tile-local' : 'meeting-tile-remote'}
    >
      {hasVideo && isTrackReference(trackRef) ? (
        <VideoTrack trackRef={trackRef} style={FILL_VIDEO_STYLE} />
      ) : (
        <Center position="absolute" top={0} left={0} right={0} bottom={0} bg="$bgDark">
          <Text size={initialsSize} color="$textHint" fontWeight="bold">
            {participantInitials(trackRef.participant)}
          </Text>
        </Center>
      )}
      {showNameBadge ? (
        <Box
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          px="$2"
          py="$1"
          bg="rgba(0,0,0,0.55)"
        >
          <HStack alignItems="center" justifyContent="space-between">
            <Text size="xs" color="$textInverted" numberOfLines={1} flex={1}>
              {label}
            </Text>
            {isMuted ? (
              <Icon as={MicOff} size="xs" color="$statusError" />
            ) : null}
          </HStack>
        </Box>
      ) : null}
    </Box>
  );
}

const CONTAIN_VIDEO_STYLE = {
  ...FILL_VIDEO_STYLE,
  objectFit: 'contain' as const,
};

/** Render a single track that fills its (positioned) parent, e.g. a screen share. */
export function MeetingFillVideo({
  trackRef,
  contain = false,
}: {
  trackRef: TrackReferenceOrPlaceholder;
  /** Prefer contain for screen-share PiPs so slide/text content stays readable. */
  contain?: boolean;
}) {
  if (!isTrackReference(trackRef)) return null;
  return (
    <VideoTrack
      trackRef={trackRef}
      style={contain ? CONTAIN_VIDEO_STYLE : FILL_VIDEO_STYLE}
    />
  );
}

function useCameraTracks() {
  // withPlaceholder keeps a tile (initials) for participants whose camera is off.
  return useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: true }],
    { onlySubscribed: true },
  );
}

/**
 * Speaker view: remote camera fills the stage. Local camera and any remote
 * screen share sit in corner PiPs — share never steals the main stage
 * (proctoring / desk share stays secondary to the person).
 */
export function MeetingSpeakerView() {
  const cameraTracks = useCameraTracks();
  const screenTracks = useTracks([Track.Source.ScreenShare], { onlySubscribed: true });
  const remoteTrack = cameraTracks.find((tr) => !tr.participant.isLocal);
  const localTrack = cameraTracks.find((tr) => tr.participant.isLocal);
  const remoteShare = screenTracks.find((tr) => !tr.participant.isLocal) ?? null;
  const mainTrack = remoteTrack ?? localTrack ?? null;

  return (
    <Box flex={1} minHeight={0} position="relative" testID="meeting-speaker-view">
      {mainTrack ? (
        <ParticipantTile trackRef={mainTrack} rounded="lg" initialsSize="4xl" />
      ) : (
        <Box flex={1} borderRadius="$lg" bg="$bgDark" />
      )}
      {remoteShare ? (
        <Box
          position="absolute"
          bottom={12}
          right={12}
          width={220}
          height={140}
          softShadow="3"
          zIndex={11}
          borderRadius="$md"
          overflow="hidden"
          bg="$bgDark"
          borderWidth={1}
          borderColor="$borderLight"
          testID="meeting-screen-share-pip"
        >
          <MeetingFillVideo trackRef={remoteShare} contain />
        </Box>
      ) : null}
      {remoteTrack && localTrack ? (
        <Box
          position="absolute"
          bottom={12}
          {...(remoteShare
            ? { right: 12 + 220 + 8 }
            : { right: 12 })}
          width={160}
          height={120}
          softShadow="3"
          zIndex={10}
          testID="meeting-pip"
        >
          <ParticipantTile trackRef={localTrack} rounded="md" />
        </Box>
      ) : null}
    </Box>
  );
}
